import { Button } from "@/components/ui/Button";
import { useCallback, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useImportNotes, type ImportResult } from "../../hooks/useImport";
import { Icon } from "@/components/ui/Icon";
import { useUIStore } from "../../store/ui";

const ACCEPTED = ".md,.zip,.bear2bk,.textpack,.json";
const ACCEPTED_EXTENSIONS = [".md", ".zip", ".bear2bk", ".textpack", ".json"];
const FORMAT_LABELS = [
  { id: "markdown", ext: ".md", label: "Markdown files", hint: "One note per file" },
  { id: "zip", ext: ".zip", label: "ZIP archive", hint: "Folder of .md files" },
  { id: "textpack", ext: ".textpack", label: "TextBundle", hint: "Bear, Ulysses, iA Writer" },
  { id: "google-keep", ext: ".zip", label: "Google Keep", hint: "Google Takeout export" },
  { id: "bear", ext: ".bear2bk", label: "Bear backup", hint: "Bear app export" },
  { id: "notion", ext: ".json", label: "Notion export", hint: "Notion JSON export" },
] as const;

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/** Settings → Import notes. The account gate runs before this page opens. */
export function ImportSettings() {
  const { t } = useTranslation();
  const openSettings = useUIStore((s) => s.openSettings);
  const [dragging, setDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync, isPending } = useImportNotes();
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const addFiles = useCallback((files: File[]) => {
    const accepted = files.filter(isAcceptedFile);
    if (accepted.length === 0) return;
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = accepted.filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
    setResult(null);
    setImportError(null);
  }, []);

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    // reset input so same files can be re-selected
    e.target.value = "";
  };

  const handleImport = async () => {
    if (selectedFiles.length === 0 || isPending) return;
    setImportError(null);
    setResult(null);
    try {
      const r = await mutateAsync(selectedFiles);
      setResult(r);
    } catch (e) {
      setImportError((e as Error).message ?? "Import failed");
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Drop zone */}
        <div
          className={[
            "border-[1.5px] border-dashed rounded-2xl p-6 flex items-center justify-center",
            "transition-[border-color,background] duration-150 min-h-[110px]",
            dragging
              ? "border-accent bg-accent-bg cursor-default"
              : "cursor-pointer border-border hover:border-accent hover:bg-accent-bg",
          ].join(" ")}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center gap-1 text-center pointer-events-none">
            <div
              className={[
                "w-10 h-10 rounded-2xl flex items-center justify-center mb-1",
                "transition-[background,color] duration-150",
                dragging
                  ? "bg-accent-bg text-accent"
                  : "bg-border-light text-muted",
              ].join(" ")}
            >
              <Icon name="upload" size={20} />
            </div>
            <p className="text-[13px] text-ink-secondary font-medium m-0">
              {dragging ? t("importModal.dropToImport") : t("importModal.dropOrClick")}
            </p>
            <p className="text-[11px] text-muted m-0">
              {FORMAT_LABELS.map((f) => f.ext).join(", ")}
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          className="sr-only"
          onChange={handleFileChange}
        />

        {/* Selected files list */}
        {selectedFiles.length > 0 && (
          <div className="rounded-2xl overflow-hidden divide-y divide-border-light">
            {selectedFiles.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-3 text-[13px] py-2 px-3 bg-sidebar"
              >
                <Icon name="note" size={14} className="shrink-0 text-accent" />
                <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-ink-secondary font-medium">
                  {file.name}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-6 h-6 shrink-0"
                  onClick={() => removeFile(i)}
                  title={t("importModal.removeFile")}
                >
                  <Icon name="close" size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Supported formats */}
        {selectedFiles.length === 0 && (
          <div className="rounded-2xl overflow-hidden divide-y divide-border-light">
            {FORMAT_LABELS.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-3 text-xs py-2 px-2 bg-sidebar"
              >
                <code className="font-code text-[11px] text-accent-text bg-accent-bg py-[2px] px-[6px] rounded-full min-w-[68px] text-center">
                  {f.ext}
                </code>
                <span className="font-medium text-ink-secondary flex-1">
                  {f.label}
                </span>
                <span className="text-muted text-[11px]">{f.hint}</span>
              </div>
            ))}
          </div>
        )}

        {selectedFiles.length === 0 && (
          <p className="text-[12px] leading-5 text-muted m-0">
            {t("importModal.keepNote")}
          </p>
        )}

        {/* Result / error feedback */}
        {result !== null && (
          <div className="flex flex-col gap-1 py-2.5 px-3 rounded-2xl text-[13px] bg-accent-bg text-accent-text border border-primary">
            <div className="flex items-center gap-2">
              <Icon name="check-circle" size={14} className="shrink-0" />
              <span>
                <Trans
                  i18nKey="importModal.importedCount"
                  count={result.imported}
                  values={{ count: result.imported }}
                  components={{ strong: <strong /> }}
                />
                {result.source ? t("importModal.fromSource", { source: result.source.replace("-", " ") }) : ""}
              </span>
            </div>
            {(result.skipped > 0 || result.warnings.length > 0) && (
              <span className="text-[12px] text-accent-text/90">
                {result.skipped > 0
                  ? `Skipped ${result.skipped} item${result.skipped === 1 ? "" : "s"}. `
                  : ""}
                {result.warnings.length > 0
                  ? `${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"}.`
                  : ""}
              </span>
            )}
          </div>
        )}
        {!!importError && (
          <div className="flex items-center gap-2 py-2.5 px-3 rounded-2xl text-[13px] bg-danger-bg text-danger border border-danger-muted/20">
            <Icon name="alert" size={14} className="shrink-0" />
            <span>{importError}</span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        {result ? (
          <Button size="sm" onClick={() => openSettings("root")}>
            {t("common.done")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={selectedFiles.length === 0 || isPending}
            onClick={handleImport}
          >
            {isPending
              ? t("importModal.importing")
              : selectedFiles.length > 1
                ? t("importModal.importN", { count: selectedFiles.length })
                : t("importModal.importBtn")}
          </Button>
        )}
      </div>
    </>
  );
}
